import AWS from 'aws-sdk';
import accountService from 'services/account';
import userService from 'services/user';
import { configureAwsCognitoIdentityStorage } from 'utils/auth-storage';
import { v4 as uuid } from 'uuid';

const S3_BUCKET = process.env.REACT_APP_S3_BUCKET;
const AWS_REGION = process.env.REACT_APP_AWS_REGION;
const AWS_IDENTITY_POOL_ID = process.env.REACT_APP_AWS_IDENTITY_POOL_ID;

const tokenCache = new Map(); // Cache to store tokens per account
const SANITIZABLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const IMAGE_TYPES_BY_EXTENSION = new Map([
    ['jpeg', 'image/jpeg'],
    ['jpg', 'image/jpeg'],
    ['png', 'image/png'],
    ['webp', 'image/webp'],
]);

// Fetch or return cached account token
const getCachedAccountToken = async (ownerAccountId) => {
    const cachedToken = tokenCache.get(ownerAccountId);

    if (cachedToken && cachedToken.expiresAt > Date.now()) {
        return cachedToken;
    }

    const accountTokens = await accountService.getAccountTokens(ownerAccountId);
    const tokenExpiryTime = Date.now() + 55 * 60 * 1000; // 55 minutes

    tokenCache.set(ownerAccountId, {
        IdentityId: accountTokens.IdentityId,
        Token: accountTokens.Token,
        expiresAt: tokenExpiryTime,
    });

    return accountTokens;
};

// Fetch or return cached AWS credentials
let cachedCredentials = null;
const getAwsCredentials = async (accountId) => {
    configureAwsCognitoIdentityStorage(AWS);

    if (cachedCredentials && !cachedCredentials.needsRefresh()) {
        return cachedCredentials;
    }

    const accountTokens = await getCachedAccountToken(accountId);
    cachedCredentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: AWS_IDENTITY_POOL_ID,
        IdentityId: accountTokens.IdentityId,
        Logins: { 'cognito-identity.amazonaws.com': accountTokens.Token },
    });

    await cachedCredentials.getPromise(); // Ensure credentials are ready
    return cachedCredentials;
};

const loadImage = (url) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Image could not be read.'));
        image.src = url;
    });
};

const canvasToBlob = (canvas, type) => {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Image could not be sanitized.'));
                    return;
                }

                resolve(blob);
            },
            type,
            type === 'image/jpeg' ? 0.92 : undefined
        );
    });
};

const getSanitizableImageType = (file) => {
    if (SANITIZABLE_IMAGE_TYPES.has(file.type)) {
        return file.type;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    return IMAGE_TYPES_BY_EXTENSION.get(extension);
};

const sanitizeImageFile = async (file) => {
    const sanitizedImageType = getSanitizableImageType(file);
    const fileType = file.type || '';

    if (!fileType.startsWith('image/') && !sanitizedImageType) {
        return file;
    }

    if (!sanitizedImageType) {
        throw new Error('Unsupported image type.');
    }

    const objectUrl = URL.createObjectURL(file);

    try {
        const image = await loadImage(objectUrl);
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('Image could not be sanitized.');
        }

        context.drawImage(image, 0, 0);

        const sanitizedBlob = await canvasToBlob(canvas, sanitizedImageType);
        return new File([sanitizedBlob], file.name, {
            type: sanitizedImageType,
            lastModified: Date.now(),
        });
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

// Upload a file to S3
const uploadFile = async (file, onProgress, onSuccess, onError, accountId) => {
    try {
        AWS.config.update({ region: AWS_REGION });

        const credentials = await getAwsCredentials(accountId);
        AWS.config.update({ credentials });

        const s3 = new AWS.S3();
        const key = uuid();
        const uploadableFile = await sanitizeImageFile(file);

        s3.upload(
            {
                Bucket: S3_BUCKET,
                Key: `${key}/${uploadableFile.name}`,
                Body: uploadableFile,
                ContentType: uploadableFile.type,
            },
            (err) => {
                if (err) {
                    return onError(err);
                }
                onSuccess({ bucket: S3_BUCKET, key, type: uploadableFile.type, name: uploadableFile.name });
            }
        ).on('httpUploadProgress', (p) => onProgress({ percent: (p.loaded / p.total) * 100 }));
    } catch (err) {
        onError(err);
    }
};

// Profile images use a backend-issued URL so this upload never receives AWS credentials.
const uploadProfileImage = async (file, onProgress, onSuccess, onError, userId) => {
    try {
        const uploadableFile = await sanitizeImageFile(file);
        const {
            url,
            previewUrl,
            file: uploadedFile,
        } = await userService.createProfileImageUpload(userId, {
            name: uploadableFile.name,
            type: uploadableFile.type,
        });

        await new Promise((resolve, reject) => {
            const request = new XMLHttpRequest();
            request.open('PUT', url);
            request.setRequestHeader('Content-Type', uploadableFile.type);
            request.upload.onprogress = ({ lengthComputable, loaded, total }) => {
                if (lengthComputable && total) onProgress({ percent: (loaded / total) * 100 });
            };
            request.onload = () => {
                if (request.status >= 200 && request.status < 300) resolve();
                else reject(new Error(`Profile image upload failed with status ${request.status}.`));
            };
            request.onerror = () => reject(new Error('Profile image upload failed.'));
            request.send(uploadableFile);
        });

        await onSuccess({ ...uploadedFile, preview_url: previewUrl });
    } catch (err) {
        onError(err);
    }
};

export { uploadFile, uploadProfileImage };
